"use client";

import { useCallback, type KeyboardEvent } from "react";

import type { MarkdownCommand } from "./useInsertMarkdownAtSelection";

interface UseFolderNotesEditorKeyDownArguments {
  onChangeNotesDraft: (nextValue: string) => void;
  insertMarkdownAtSelection: (command: MarkdownCommand) => void;
}

/**
 * Handles keyboard interactions for the notes markdown editor textarea:
 * - Continue list prefixes on Enter (GitHub/GitLab-style)
 * - Cmd/Ctrl-based shortcuts for markdown formatting
 */
export function useFolderNotesEditorKeyDown({
  onChangeNotesDraft,
  insertMarkdownAtSelection,
}: UseFolderNotesEditorKeyDownArguments) {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Continue list prefixes on Enter (GitHub/GitLab-style behavior).
      if (
        event.key === "Enter" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        const target = event.target;
        if (
          !(target instanceof HTMLTextAreaElement) &&
          !(target instanceof HTMLInputElement)
        ) {
          return;
        }

        const selectionStart = target.selectionStart ?? 0;
        const selectionEnd = target.selectionEnd ?? 0;
        if (selectionStart !== selectionEnd) {
          return;
        }

        const value = target.value ?? "";
        const caretIndex = selectionStart;
        const lineStart = value.lastIndexOf("\n", caretIndex - 1) + 1;
        const nextNewlineIndex = value.indexOf("\n", caretIndex);
        const lineEnd =
          nextNewlineIndex === -1 ? value.length : nextNewlineIndex;
        const line = value.slice(lineStart, lineEnd);

        const indentMatch = line.match(/^(\s*)/);
        const indent = indentMatch?.[1] ?? "";
        const content = line.slice(indent.length);

        const checklistMatch = content.match(/^- \[( |x|X)\] /);
        const numberedMatch = content.match(/^(\d+)\. /);
        const bulletedMatch = content.startsWith("- ");

        const schedule =
          globalThis.requestAnimationFrame ??
          ((callback: FrameRequestCallback) =>
            globalThis.setTimeout(callback, 0));

        const insertNewlineWithPrefix = (prefix: string) => {
          event.preventDefault();
          const nextValue =
            value.slice(0, caretIndex) +
            "\n" +
            indent +
            prefix +
            value.slice(caretIndex);
          onChangeNotesDraft(nextValue);
          schedule(() => {
            const nextCaret = caretIndex + 1 + indent.length + prefix.length;
            target.focus();
            target.setSelectionRange(nextCaret, nextCaret);
          });
        };

        const endListAndInsertPlainNewline = () => {
          event.preventDefault();
          const nextValue =
            value.slice(0, lineStart) +
            indent +
            "\n" +
            value.slice(lineEnd + (nextNewlineIndex === -1 ? 0 : 1));

          onChangeNotesDraft(nextValue);
          schedule(() => {
            const nextCaret = lineStart + indent.length + 1;
            target.focus();
            target.setSelectionRange(nextCaret, nextCaret);
          });
        };

        // Checklist
        if (checklistMatch) {
          const prefix = "- [ ] ";
          const isEmptyItem =
            content.replace(/^- \[( |x|X)\] /, "").trim().length === 0;
          if (isEmptyItem) {
            endListAndInsertPlainNewline();
            return;
          }
          insertNewlineWithPrefix(prefix);
          return;
        }

        // Numbered list
        if (numberedMatch) {
          const currentNumber = Number.parseInt(numberedMatch[1] ?? "1", 10);
          const isEmptyItem =
            content.replace(/^\d+\. /, "").trim().length === 0;
          if (isEmptyItem) {
            endListAndInsertPlainNewline();
            return;
          }
          insertNewlineWithPrefix(`${currentNumber + 1}. `);
          return;
        }

        // Bulleted list (must come after checklist detection)
        if (bulletedMatch) {
          const isEmptyItem = content.replace(/^- /, "").trim().length === 0;
          if (isEmptyItem) {
            endListAndInsertPlainNewline();
            return;
          }
          insertNewlineWithPrefix("- ");
          return;
        }
      }

      const key = event.key.toLowerCase();
      const code = event.code;
      const hasModifierKey = event.metaKey || event.ctrlKey;
      if (!hasModifierKey) return;

      const preventAndInsert = (command: MarkdownCommand) => {
        event.preventDefault();
        insertMarkdownAtSelection(command);
      };

      // Bold / Italic / Link
      if (!event.shiftKey && !event.altKey) {
        if (key === "b") {
          preventAndInsert({ type: "wrap", before: "**", after: "**" });
          return;
        }
        if (key === "i") {
          preventAndInsert({ type: "wrap", before: "_", after: "_" });
          return;
        }
        if (key === "k") {
          preventAndInsert({ type: "wrap", before: "[", after: "](url)" });
          return;
        }
      }

      // Headings: Cmd+Opt+1/2/3 or Ctrl+Alt+1/2/3
      if (event.altKey && !event.shiftKey) {
        if (code === "Digit1" || code === "Numpad1") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "# ",
            replaceExistingHeadingPrefix: true,
          });
          return;
        }
        if (code === "Digit2" || code === "Numpad2") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "## ",
            replaceExistingHeadingPrefix: true,
          });
          return;
        }
        if (code === "Digit3" || code === "Numpad3") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "### ",
            replaceExistingHeadingPrefix: true,
          });
          return;
        }
      }

      // Lists / Quote / Code
      if (event.shiftKey && !event.altKey) {
        if (code === "Digit7" || code === "Numpad7") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "1. ",
            insertOnNewLineIfTextBeforeCaret: true,
          });
          return;
        }
        if (code === "Digit8" || code === "Numpad8") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "- ",
            insertOnNewLineIfTextBeforeCaret: true,
          });
          return;
        }
        if (code === "Digit9" || code === "Numpad9") {
          preventAndInsert({
            type: "line_prefix",
            prefix: "- [ ] ",
            toggleOffPrefixes: ["- [x] ", "- [X] "],
            insertOnNewLineIfTextBeforeCaret: true,
          });
          return;
        }
        if (code === "Period") {
          preventAndInsert({ type: "line_prefix", prefix: "> " });
          return;
        }
        // Slack-style: Cmd/Ctrl+Shift+C for inline code
        if (key === "c") {
          preventAndInsert({ type: "wrap", before: "`", after: "`" });
          return;
        }
      }
    },
    [insertMarkdownAtSelection, onChangeNotesDraft],
  );
}
